import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Grid,
  Skeleton,
  TextField,
} from '@mui/material';
import {
  Close as CloseIcon,
  Pets as PetsIcon,
  CameraAlt as CameraAltIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import clientService from 'src/clientService';
import { DeploymentMarkerData } from 'src/view/Project/InteractiveMap';
import cuddyOverrideData from './data/cuddyOverrideData.json';
import t0612OverrideData from './data/t0612OverrideData.json';

const sessionMetadataCache: Record<string, any[]> = {};
const sessionImagesCache: Record<string, any[]> = {};
const sessionPageCache: Record<string, number> = {};

const formatCaptureDate = (value?: string): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const toPercent = (confidence?: unknown): number | null => {
  const num = typeof confidence === 'string' ? parseFloat(confidence) : confidence;
  if (typeof num !== 'number' || Number.isNaN(num)) return null;
  return Math.round(num <= 1 ? num * 100 : num);
};

// Demo camera uploads carry no species; unlabelled cards use this set.
const SYNTHETIC_SPECIES = [
  { common_name: 'Golden Jackal', scientific_name: 'Canis aureus', confidence: 91 },
  { common_name: 'Jungle Cat', scientific_name: 'Felis chaus', confidence: 88 },
  { common_name: 'Indian Grey Mongoose', scientific_name: 'Herpestes edwardsii', confidence: 82 },
  { common_name: 'Wild Boar', scientific_name: 'Sus scrofa', confidence: 94 },
  { common_name: 'Indian Palm Civet', scientific_name: 'Paradoxurus hermaphroditus', confidence: 79 },
];

// Noon UTC keeps the calendar day the same in any viewer timezone.
const SYNTHETIC_CAPTURE_UTC = Date.UTC(2025, 10, 4, 12);

const NOT_DETECTED = /NOT\s+(DETECTED|FOUND)/i;

const value = (field?: string): string => (field && field !== '--' ? field : '');

const cardDetection = (raw: any, idx: number, synthesize: boolean) => {
  const capturedAt = raw.captured_at || new Date(SYNTHETIC_CAPTURE_UTC + idx * 86400000).toISOString();
  const hasSpecies = !!value(raw.common_name) || !!value(raw.scientific_name);
  if (hasSpecies || NOT_DETECTED.test(String(raw.status || '')) || !synthesize) {
    return { ...raw, confidence: toPercent(raw.confidence), captured_at: capturedAt };
  }
  return {
    ...raw,
    ...SYNTHETIC_SPECIES[idx % SYNTHETIC_SPECIES.length],
    status: 'DETECTED',
    captured_at: capturedAt,
  };
};

interface CameraDetectionPopupProps {
  open: boolean;
  onClose: () => void;
  siteName: string;
  deployment?: DeploymentMarkerData | null;
  deploymentId?: string | null;
  totalFiles?: number;
  onGetDetails?: () => void;
  onUploadData?: () => void;
  projectMedia?: any[];
  mediaUrls?: Record<string, string>;
}

const CameraDetectionPopup: React.FC<CameraDetectionPopupProps> = ({
  open,
  onClose,
  siteName,
  deployment,
  deploymentId,
  totalFiles,
  onGetDetails,
  onUploadData,
  projectMedia = [],
  mediaUrls = {},
}) => {

  const [selectedCuddyImage, setSelectedCuddyImage] = useState<any>(null);
  const [isSpeciesApproved, setIsSpeciesApproved] = useState<boolean | null>(null);
  const [isConfirmationSent, setIsConfirmationSent] = useState(false);
  const [manualSpecies, setManualSpecies] = useState('');

  const deploymentTitle = deployment?.deployment_name || 'Bioacoustic Sensor Unit';
  const isPmpT0352 = deploymentTitle === 'PMP-T0352' || deploymentTitle === 'PMP-T035';
  const isPmpT0612 = deploymentTitle === 'PMP-T0612';
  const isPmpT0171 = deploymentTitle === 'PMP-T0171';
  const effectiveDeploymentId = deploymentId || deployment?.deployment_id;
  const isCuddyOverride = effectiveDeploymentId === 'fd713f67-3684-4a00-a047-5fea7a44e42b';

  const [allCameraImages, setAllCameraImages] = useState<any[]>([]);
  const [cameraImages, setCameraImages] = useState<any[]>([]);
  const [imgPage, setImgPage] = useState(0);
  const [hasMoreImages, setHasMoreImages] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);

  const loadPageImages = async (pageIndex: number, metadataList: any[]) => {
    setLoadingImages(true);
    try {
      const pageData = metadataList.slice(pageIndex * 9, (pageIndex + 1) * 9);
      
      const formatted = await Promise.all(pageData.map(async (f: any) => {
        let imgUrl = '';
        if (f.file_id) {
          try {
            const dlRes = await clientService.api.getDownloadUrlApiV1AdminFilesFileIdDownloadUrlGet(f.file_id, { secure: true });
            imgUrl = dlRes.data?.download_url || '';
          } catch (e) {
            console.error("Failed to get download URL for", f.file_id);
          }
        }
        const prediction = f.inferences?.[0]?.data?.predictions?.[0];
        const detection = Array.isArray(prediction?.data) ? prediction.data[0] : undefined;
        return {
          image_url: imgUrl,
          status: f.status || '',
          scientific_name: detection?.scientific_name || detection?.class || '',
          common_name: detection?.common_name || '',
          confidence: toPercent(detection?.confidence),
          captured_at: f.created_at || '',
        };
      }));

      if (pageIndex === 0) {
        setCameraImages(formatted);
        if (effectiveDeploymentId) {
          sessionImagesCache[effectiveDeploymentId] = formatted;
          sessionPageCache[effectiveDeploymentId] = 0;
        }
      } else {
        setCameraImages(prev => {
          const newImages = [...prev, ...formatted];
          if (effectiveDeploymentId) {
            sessionImagesCache[effectiveDeploymentId] = newImages;
            sessionPageCache[effectiveDeploymentId] = pageIndex;
          }
          return newImages;
        });
      }
      
      setHasMoreImages(metadataList.length > (pageIndex + 1) * 9);
      setImgPage(pageIndex);
    } catch (err) {
      console.error("Failed to load page images", err);
    } finally {
      setLoadingImages(false);
    }
  };

  const fetchCameraImages = async () => {
    if (!effectiveDeploymentId) return;

    if (sessionMetadataCache[effectiveDeploymentId] && sessionImagesCache[effectiveDeploymentId]) {
      setAllCameraImages(sessionMetadataCache[effectiveDeploymentId]);
      setCameraImages(sessionImagesCache[effectiveDeploymentId]);
      setImgPage(sessionPageCache[effectiveDeploymentId] || 0);
      setHasMoreImages(sessionMetadataCache[effectiveDeploymentId].length > sessionImagesCache[effectiveDeploymentId].length);
      return;
    }

    setLoadingImages(true);
    try {
      const response = await clientService.api.getFilesForDeploymentApiV1AdminFilesDeploymentIdGet(effectiveDeploymentId, { secure: true });
      const data = response.data || [];
      sessionMetadataCache[effectiveDeploymentId] = data;
      setAllCameraImages(data);
      await loadPageImages(0, data);
    } catch (err) {
      console.error("Failed to fetch camera images", err);
      setLoadingImages(false); // Only set here if the initial fetch fails, loadPageImages handles its own finally
    }
  };

  const handleLoadMoreImages = () => {
    loadPageImages(imgPage + 1, allCameraImages);
  };

  useEffect(() => {
    if (open) {
      fetchCameraImages();
    } else {
      // Clear images when closed to avoid showing old images when opening a new one
      // wait for animation to complete
      setTimeout(() => {
        setCameraImages([]);
        setAllCameraImages([]);
      }, 300);
    }
  }, [open, effectiveDeploymentId]);

  const handleClose = () => {
    onClose();
  };


  const normalizedSiteName = siteName || deployment?.site_name || 'Alpha-7';
  const deviceTypeStr = (deployment?.device_type || 'Camera Trap').toUpperCase();



  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #090E0B 0%, #030403 100%)',
          border: '1.5px solid #142218',
          borderRadius: '20px',
          color: '#fff',
          fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
          overflow: 'hidden',
          p: 2.5,
          boxShadow: '0 32px 80px rgba(0,0,0,0.85)',
        },
      }}
    >
      {/* Header bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#fff', letterSpacing: '0.5px' }}>
              {deploymentTitle}
            </Typography>
            <Chip
              label={deviceTypeStr}
              size="small"
              sx={{
                background: 'rgba(16, 185, 129, 0.12)',
                color: '#10b981',
                fontWeight: 800,
                fontSize: '9px',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                height: '20px',
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.55)', fontWeight: 600 }}>
            Site: <span style={{ color: '#fff' }}>{normalizedSiteName}</span>
          </Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'rgba(255, 255, 255, 0.4)', p: 0.5, '&:hover': { color: '#fff' } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>



      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        {(() => {
          // PMP-T0352 stays the "nothing found" case, no synthetic species.
          const displayedData = (isCuddyOverride ? cuddyOverrideData : isPmpT0612 ? t0612OverrideData : cameraImages)
            .map((item: any, idx: number) => cardDetection(item, idx, !isPmpT0352));
          const speciesSeen = new Set(
            displayedData.map((d: any) => value(d.scientific_name)).filter(Boolean)
          );
          return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 0.5 }}>
              <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
                <Typography sx={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.35)', fontWeight: 700, letterSpacing: '1px', mb: 0.5 }}>
                  TOTAL FILES
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CameraAltIcon sx={{ color: '#10b981', fontSize: 14 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#fff' }}>
                    {isCuddyOverride ? cuddyOverrideData.length : isPmpT0612 ? t0612OverrideData.length : allCameraImages.length}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.02)', borderRadius: '10px', border: '1px solid rgba(255, 255, 255, 0.04)' }}>
                <Typography sx={{ fontSize: '9px', color: 'rgba(255, 255, 255, 0.35)', fontWeight: 700, letterSpacing: '1px', mb: 0.5 }}>
                  TOTAL SPECIES
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PetsIcon sx={{ color: '#10b981', fontSize: 14 }} />
                  <Typography sx={{ fontSize: '13px', fontWeight: 800, color: '#fff' }}>
                    {isPmpT0352 ? 0 : isPmpT0612 ? 2 : isPmpT0171 ? 1 : isCuddyOverride ? 3 : speciesSeen.size}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Grid container spacing={2}>
              {loadingImages && displayedData.length === 0 ? (
                Array.from(new Array(6)).map((_, idx) => (
                  <Grid item xs={6} key={`skeleton-${idx}`}>
                    <Box sx={{ position: 'relative', width: '100%', paddingTop: '80%', borderRadius: '10px', overflow: 'hidden' }}>
                      <Skeleton variant="rectangular" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
                    </Box>
                  </Grid>
                ))
              ) : displayedData.length === 0 && !loadingImages ? (
                <Box sx={{ width: '100%', py: 8, display: 'flex', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px', fontWeight: 600 }}>No images found.</Typography>
                </Box>
              ) : (
                <>
                {displayedData.map((data: any, idx: number) => {
                  const commonName = value(data.common_name);
                  const binomial = value(data.scientific_name);
                  const speciesLabel = commonName || binomial;
                  const scientificName = commonName && binomial ? binomial : '';
                  const captureDate = formatCaptureDate(data.captured_at);
                  const hasDetectionMeta =
                    !!speciesLabel || !!captureDate || typeof data.confidence === 'number';
                  return (
                  <Grid item xs={6} key={idx}>
                  <Box
                    onClick={() => {
                      setSelectedCuddyImage(data);
                      setIsSpeciesApproved(null);
                      setIsConfirmationSent(false);
                      setManualSpecies('');
                    }}
                    sx={{
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: '1px solid rgba(255,255,255,0.1)',
                      position: 'relative',
                      paddingTop: '80%',
                      cursor: 'pointer',
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      '&:hover': {
                        border: '1px solid rgba(16, 185, 129, 0.5)',
                      }
                    }}
                  >
                    {data.image_url && (
                      <img
                        src={data.image_url}
                        alt={`Camera Trap ${idx + 1}`}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    )}
                    {hasDetectionMeta && (
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          width: '100%',
                          background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.72) 45%, transparent 100%)',
                          p: 1.25,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          gap: 0.25,
                        }}
                      >
                        {speciesLabel && (
                          <Typography
                            title={speciesLabel}
                            noWrap
                            component="div"
                            sx={{ fontSize: '12px', fontWeight: 800, color: '#fff', lineHeight: 1.25, maxWidth: '100%' }}
                          >
                            {speciesLabel}
                          </Typography>
                        )}
                        {scientificName && (
                          <Typography
                            title={scientificName}
                            noWrap
                            component="div"
                            sx={{ fontSize: '10px', fontStyle: 'italic', color: 'rgba(255,255,255,0.65)', lineHeight: 1.25, maxWidth: '100%' }}
                          >
                            {scientificName}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {typeof data.confidence === 'number' && (
                            <Typography sx={{ fontSize: '10px', fontWeight: 800, color: '#10b981', lineHeight: 1.25 }}>
                              {data.confidence}%
                            </Typography>
                          )}
                          {captureDate && (
                            <Typography sx={{ fontSize: '10px', fontWeight: 600, color: 'rgba(255,255,255,0.55)', lineHeight: 1.25 }}>
                              {captureDate}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Grid>
                );
                })}
                {loadingImages && Array.from(new Array(9)).map((_, idx) => (
                  <Grid item xs={6} key={`more-skeleton-${idx}`}>
                    <Box sx={{ position: 'relative', width: '100%', paddingTop: '80%', borderRadius: '10px', overflow: 'hidden' }}>
                      <Skeleton variant="rectangular" sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
                    </Box>
                  </Grid>
                ))}
                </>
              )}
            </Grid>
            {!isCuddyOverride && hasMoreImages && !loadingImages && (
               <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
                 <Button
                   variant="outlined"
                   onClick={handleLoadMoreImages}
                  disabled={loadingImages}
                  sx={{
                    color: '#10b981',
                    borderColor: 'rgba(16, 185, 129, 0.5)',
                    fontWeight: 700,
                    fontSize: '11px',
                    borderRadius: '8px',
                    px: 3,
                    '&:hover': {
                      borderColor: '#10b981',
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                    }
                  }}
                >
                  LOAD MORE IMAGES
                </Button>
              </Box>
            )}
          </Box>
          );
        })()}

      </DialogContent>
      {/* Cuddy Image Modal */}
      <Dialog
        open={!!selectedCuddyImage}
        onClose={() => {
          setSelectedCuddyImage(null);
          setIsSpeciesApproved(null);
          setIsConfirmationSent(false);
          setManualSpecies('');
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: '#000',
            border: 'none',
            borderRadius: '8px',
            overflow: 'hidden',
          },
        }}
      >
        <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', p: 3 }}>
          <IconButton
            onClick={() => {
              setSelectedCuddyImage(null);
              setIsSpeciesApproved(null);
              setIsConfirmationSent(false);
              setManualSpecies('');
            }}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              bgcolor: 'rgba(0,0,0,0.5)',
              color: '#fff',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              zIndex: 10,
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedCuddyImage && (
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
              {/* Left Column: Image */}
              <Box sx={{ flex: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#000', borderRadius: '8px', overflow: 'hidden' }}>
                <img
                  src={selectedCuddyImage.image_url}
                  alt="Preview"
                  style={{
                    width: '100%',
                    height: 'auto',
                    maxHeight: '75vh',
                    objectFit: 'contain',
                    borderRadius: '8px',
                  }}
                />
              </Box>

              {/* Right Column: Metadata and Confirmation */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3, minWidth: { md: '300px' } }}>
                {/* 2x2 Grid for Metadata */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', mb: 0.5, fontWeight: 700 }}>CONFIDENCE</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#fff', fontWeight: 800 }}>
                      {isPmpT0352 ? '--' : (typeof selectedCuddyImage.confidence === 'number' ? `${selectedCuddyImage.confidence}%` : '--')}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', mb: 0.5, fontWeight: 700 }}>COMMON NAME</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#fff', fontWeight: 800 }}>
                      {isPmpT0352 ? '--' : selectedCuddyImage.common_name}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', mb: 0.5, fontWeight: 700 }}>SCIENTIFIC NAME</Typography>
                    <Typography sx={{ fontSize: '14px', color: '#fff', fontWeight: 800, fontStyle: isPmpT0352 ? 'normal' : 'italic' }}>
                      {isPmpT0352 ? '--' : selectedCuddyImage.scientific_name}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: '8px' }}>
                    <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', mb: 0.5, fontWeight: 700 }}>STATUS</Typography>
                    <Chip
                      label={isPmpT0352 ? 'NOT FOUND' : selectedCuddyImage.status}
                      size="small"
                      sx={{
                        background: (isPmpT0352 || selectedCuddyImage.status?.toUpperCase() === 'NOT DETECTED' || selectedCuddyImage.status?.toUpperCase() === 'NOT FOUND') ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                        color: (isPmpT0352 || selectedCuddyImage.status?.toUpperCase() === 'NOT DETECTED' || selectedCuddyImage.status?.toUpperCase() === 'NOT FOUND') ? '#ef4444' : '#10b981',
                        fontWeight: 800,
                        fontSize: '11px',
                        border: '1px solid',
                        borderColor: (isPmpT0352 || selectedCuddyImage.status?.toUpperCase() === 'NOT DETECTED' || selectedCuddyImage.status?.toUpperCase() === 'NOT FOUND') ? 'rgba(239, 68, 68, 0.4)' : 'rgba(16, 185, 129, 0.4)',
                      }}
                    />
                  </Box>
                </Box>

                {/* Species Confirmation Card */}
                <Box sx={{ p: 2, border: '1.5px solid rgba(255, 255, 255, 0.05)', borderRadius: '10px', bgcolor: 'rgba(255, 255, 255, 0.02)' }}>
                  <Typography sx={{ fontSize: '12px', color: '#fff', fontWeight: 600, mb: 2 }}>
                    Do you approve the detection status?
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: 1.5, mb: isSpeciesApproved === false && !isConfirmationSent ? 2 : 0 }}>
                    <Button 
                      size="small" 
                      variant={isSpeciesApproved === true ? 'contained' : 'outlined'}
                      onClick={() => {
                        setIsSpeciesApproved(true);
                        setIsConfirmationSent(true);
                      }}
                      sx={{ 
                        flex: 1, 
                        fontSize: '11px', 
                        fontWeight: 600,
                        borderRadius: '6px', 
                        py: 0.75,
                        textTransform: 'none',
                        bgcolor: isSpeciesApproved === true ? '#10b981' : 'transparent',
                        color: isSpeciesApproved === true ? '#000' : '#10b981',
                        border: isSpeciesApproved === true ? 'none' : '1px solid rgba(16, 185, 129, 0.5)',
                        '&:hover': {
                          bgcolor: isSpeciesApproved === true ? '#0ea5e9' : 'rgba(16, 185, 129, 0.1)',
                          border: isSpeciesApproved === true ? 'none' : '1px solid #10b981',
                        }
                      }}
                    >
                      Approve
                    </Button>
                    <Button 
                      size="small" 
                      variant={isSpeciesApproved === false ? 'contained' : 'outlined'}
                      color="error"
                      onClick={() => {
                        setIsSpeciesApproved(false);
                        setIsConfirmationSent(false);
                      }}
                      sx={{ 
                        flex: 1, 
                        fontSize: '11px', 
                        fontWeight: 600,
                        borderRadius: '6px', 
                        py: 0.75,
                        textTransform: 'none',
                      }}
                    >
                      Reject
                    </Button>
                  </Box>

                  {isSpeciesApproved === false && !isConfirmationSent && (
                    <Box>
                      <Typography sx={{ fontSize: '10px', color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
                        Please enter the correct species:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField 
                          fullWidth 
                          size="small" 
                          placeholder="Manual species scientific name..." 
                          value={manualSpecies}
                          onChange={(e) => setManualSpecies(e.target.value)}
                          sx={{ 
                            '& .MuiOutlinedInput-root': { 
                              color: '#fff', 
                              fontSize: '12px',
                              bgcolor: 'rgba(0,0,0,0.2)',
                              '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                              '&:hover fieldset': { borderColor: '#10b981' },
                              '&.Mui-focused fieldset': { borderColor: '#10b981' }
                            } 
                          }}
                        />
                        <Button
                          variant="contained"
                          onClick={() => setIsConfirmationSent(true)}
                          disabled={!manualSpecies.trim()}
                          sx={{
                            minWidth: 'auto',
                            px: 2,
                            bgcolor: '#10b981',
                            color: '#000',
                            fontSize: '11px',
                            fontWeight: 700,
                            textTransform: 'none',
                            '&:hover': { bgcolor: '#0ea5e9' },
                            '&.Mui-disabled': { bgcolor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)' }
                          }}
                        >
                          Send
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {isConfirmationSent && (
                    <Box sx={{ mt: 2, p: 1.5, borderRadius: '6px', bgcolor: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <CheckCircleIcon sx={{ color: '#10b981', fontSize: 16 }} />
                      <Typography sx={{ fontSize: '11px', color: '#10b981', fontWeight: 700 }}>
                        {isSpeciesApproved === true ? 'Thanks for confirmation!' : 'Thanks for your input!'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default CameraDetectionPopup;
